import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { ErrorNotice, KeyValue, LineTable, Pill, Spinner } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can, canManagePurchaseCost } from '../../lib/permissions';
import { dateLabel, money, num } from '../../lib/format';
import type { Bom, BomLine, BomOperation, Item, ItemType } from '../../lib/types';

const TYPE_LABELS: Record<ItemType, string> = {
  product: '成品', raw_material: '原材料', service: '服务', consumable: '耗材',
};

const POLICY_LABELS: Record<string, string> = {
  powder: '合金粉末', consumable: '机加耗材', normal: '普通物料', none: '不进库存',
};

function useItemGroupOptions() {
  const [groups, setGroups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.list<string>('/api/item-groups').then((rows) => { if (!cancelled) setGroups(rows); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : '读取物料组失败'); });
    return () => { cancelled = true; };
  }, []);
  return { groups, error };
}

// —— 关联 BOM：以该物料为成品的 BOM，展开显示工序 ——
function BomOperations({ bomId }: { bomId: string }) {
  const { data, loading, error } = useDetail<Bom>(`/api/boms/${bomId}`);
  if (loading) return <div style={{ padding: 8 }}><Spinner label="加载工序…" /></div>;
  if (error) return <div style={{ padding: 8 }} className="notice bad">{error}</div>;
  const ops = data?.operations ?? [];
  const comps = data?.lines ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
      <div>
        <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>工序（{ops.length}）</div>
        <LineTable<BomOperation>
          rows={ops}
          emptyText="该 BOM 暂无工序"
          columns={[
            { header: '工序', render: (o) => `${o.operation_code ?? ''} ${o.operation_name ?? ''}`.trim() || '—' },
            { header: '工位', render: (o) => o.workstation || '—' },
            { header: '工时(分)', align: 'right', render: (o) => <span className="num">{num(o.time_minutes, 0)}</span> },
            { header: '时薪', align: 'right', render: (o) => <span className="num">{money(o.hourly_cost, 'USD')}</span> },
            { header: '质检', render: (o) => o.qc_required ? <Pill tone="warn" label="需质检" /> : <span className="muted">—</span> },
          ]}
        />
      </div>
      {comps.length ? (
        <div>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>用料（{comps.length}）</div>
          <LineTable<BomLine>
            rows={comps}
            columns={[
              { header: '物料', render: (c) => <span className="clip" style={{ maxWidth: '32ch' }} title={`${c.item_code ?? ''} ${c.item_name_zh ?? ''}`.trim()}>{`${c.item_code ?? ''} ${c.item_name_zh ?? ''}`.trim() || '—'}</span> },
              { header: '数量', align: 'right', render: (c) => <span className="num">{num(c.quantity, 3)}</span> },
              { header: '单位', render: (c) => <span className="muted">{c.uom}</span> },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

function RelatedBoms({ code }: { code: string }) {
  const [boms, setBoms] = useState<Bom[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // 首帧后拉取（避免在 render 中 setState）
  useFetchOnce(code, nonce, setLoading, setError, setBoms);

  const reload = () => setNonce((n) => n + 1);

  return (
    <div>
      <div className="detail-head" style={{ marginBottom: 4 }}>
        <h3 style={{ fontSize: 14 }}>关联 BOM / 工序</h3>
        <div className="spacer" />
        <button className="btn link" onClick={reload}>刷新</button>
      </div>
      {loading && boms === null ? <Spinner label="检索该物料的 BOM…" /> : null}
      {error ? <ErrorNotice message={error} onRetry={reload} /> : null}
      {boms && boms.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>该物料暂无以其为成品的 BOM。</div> : null}
      {boms?.map((b) => (
        <div key={b.id} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', marginBottom: 8, overflow: 'hidden' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer', background: open === b.id ? 'var(--panel-inset)' : 'transparent' }}
            onClick={() => setOpen((cur) => (cur === b.id ? null : b.id))}
          >
            <span className="num strong">{b.number}</span>
            <span className="muted" style={{ fontSize: 12 }}>版本 {b.version}</span>
            <Pill tone={b.active ? 'ok' : 'neutral'} label={b.active ? '启用' : '停用'} />
            <div className="spacer" />
            <span className="muted" style={{ fontSize: 12 }}>{open === b.id ? '收起' : '查看工序'}</span>
          </div>
          {open === b.id ? <div style={{ padding: '0 10px 10px' }}><BomOperations bomId={b.id} /></div> : null}
        </div>
      ))}
    </div>
  );
}

// 轻量一次性拉取 hook（物料详情内用）
function useFetchOnce(
  code: string,
  nonce: number,
  setLoading: (v: boolean) => void,
  setError: (v: string | null) => void,
  setBoms: (v: Bom[]) => void,
) {
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.list<Bom>('/api/boms', { q: code, limit: 50 })
      .then((rows) => { if (!cancelled) setBoms(rows); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : '加载失败'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, nonce]);
}

// —— 物料详情（查看 + 管理员就地编辑） ——
const ITEM_EDIT_FIELDS = [
  'code', 'name_zh', 'name_es', 'description_es', 'item_type',
  'stock_uom', 'currency', 'sale_price', 'purchase_cost', 'item_group', 'active',
  'inventory_policy', 'minimum_qty', 'reorder_qty', 'warning_enabled',
] as const;

function ItemDetail({ row, onSaved }: { row: Item; onSaved: () => void }) {
  const { isAdmin, user } = useAuth();
  const seeCost = canManagePurchaseCost(user);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string | number>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const { groups: itemGroups, error: groupError } = useItemGroupOptions();

  const startEdit = () => {
    setDraft({
      code: row.code, name_zh: row.name_zh, name_es: row.name_es ?? '', description_es: row.description_es ?? '',
      item_type: row.item_type, stock_uom: row.stock_uom, currency: row.currency,
      sale_price: row.sale_price,
      // 采购成本仅财务/管理员可见可改；其余角色连草稿都不带出该字段
      ...(seeCost ? { purchase_cost: row.purchase_cost ?? 0 } : {}),
      item_group: row.item_group ?? '', active: row.active,
      inventory_policy: row.inventory_policy ?? 'normal',
      minimum_qty: row.minimum_qty ?? 0,
      reorder_qty: row.reorder_qty ?? 0,
      warning_enabled: row.warning_enabled ?? 0,
    });
    setErr(null); setOk(false); setEditing(true);
  };
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const f of ITEM_EDIT_FIELDS) {
        if (f === 'purchase_cost' && !seeCost) continue;
        if (draft[f] !== undefined) body[f] = draft[f];
      }
      await api.patch(`/api/records/items/${row.id}`, body);
      setEditing(false); setOk(true); onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally { setSaving(false); }
  };
  const set = (k: string, v: string | number) => setDraft((d) => ({ ...d, [k]: v }));
  const tf = (k: string, label: string, type = 'text') => (
    <label className="field"><label>{label}</label>
      <input className="txt" type={type} value={String(draft[k] ?? '')} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.name_zh}</h3>
        {row.name_es ? <span className="muted">· {row.name_es}</span> : null}
        <Pill tone={row.active ? 'ok' : 'neutral'} label={row.active ? '启用' : '停用'} />
        <div className="spacer" />
        {!editing && isAdmin ? <button className="btn" onClick={startEdit}>编辑物料</button> : null}
      </div>
      {err ? <div className="notice bad">{err}</div> : null}
      {ok && !editing ? <div className="notice info">已保存，列表已刷新（写入真实核心服务）。</div> : null}

      {editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          {tf('code', '物料编码')}
          {tf('name_zh', '中文名称')}
          {tf('name_es', '西语名称')}
          <label className="field"><label>物料组</label>
            <select className="sel" value={String(draft.item_group ?? '')} onChange={(e) => set('item_group', e.target.value)}>
              <option value="">请选择物料组</option>
              {String(draft.item_group ?? '') && !itemGroups.includes(String(draft.item_group)) ? <option value={String(draft.item_group)}>当前旧分组：{String(draft.item_group)}</option> : null}
              {itemGroups.map((group) => <option key={group} value={group}>{group}</option>)}
            </select>
            {groupError ? <span className="field-hint">{groupError}</span> : null}
          </label>
          <label className="field"><label>物料类型</label>
            <select className="sel" value={String(draft.item_type)} onChange={(e) => set('item_type', e.target.value)}>
              {Object.entries(TYPE_LABELS).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
            </select>
          </label>
          <label className="field"><label>库存策略</label>
            <select className="sel" value={String(draft.inventory_policy ?? 'normal')} onChange={(e) => set('inventory_policy', e.target.value)}>
              {Object.entries(POLICY_LABELS).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
            </select>
          </label>
          {tf('stock_uom', '库存单位')}
          {tf('minimum_qty', '最低库存', 'number')}
          {tf('reorder_qty', '补货量', 'number')}
          {tf('currency', '币种')}
          {tf('sale_price', '销售价格', 'number')}
          {seeCost ? tf('purchase_cost', '采购成本', 'number') : null}
          {tf('description_es', '西语描述')}
          <label className="field"><label>启用预警</label>
            <select className="sel" value={String(draft.warning_enabled ?? 0)} onChange={(e) => set('warning_enabled', e.target.value)}>
              <option value="0">否</option>
              <option value="1">是</option>
            </select>
          </label>
          <div className="detail-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn primary" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存修改'}</button>
            <button className="btn" onClick={() => { setEditing(false); setErr(null); }} disabled={saving}>取消</button>
          </div>
        </div>
      ) : (
        <div className="kv-grid">
          <KeyValue k="物料编码">{row.code}</KeyValue>
          <KeyValue k="中文名称">{row.name_zh}</KeyValue>
          <KeyValue k="西语名称">{row.name_es || '—'}</KeyValue>
          <KeyValue k="物料组">{row.item_group || '—'}</KeyValue>
          <KeyValue k="类型">{TYPE_LABELS[row.item_type] || row.item_type}</KeyValue>
          <KeyValue k="库存策略">{POLICY_LABELS[row.inventory_policy || 'normal'] || row.inventory_policy || '普通物料'}</KeyValue>
          <KeyValue k="库存单位">{row.inventory_policy === 'powder' ? 'g' : row.stock_uom}</KeyValue>
          <KeyValue k="最低库存">{row.minimum_qty ?? 0}</KeyValue>
          <KeyValue k="补货量">{row.reorder_qty ?? 0}</KeyValue>
          <KeyValue k="库存预警">{row.warning_enabled ? '已启用' : '关闭'}</KeyValue>
          <KeyValue k="币种">{row.currency}</KeyValue>
          <KeyValue k="销售价格">{money(row.sale_price, row.currency)}</KeyValue>
          {seeCost ? <KeyValue k="采购成本">{money(row.purchase_cost ?? 0, row.currency)}</KeyValue> : null}
          <KeyValue k="序列号管理">{row.serial_tracked ? '是' : '否'}</KeyValue>
          <KeyValue k="批次管理">{row.batch_tracked ? '是' : '否'}</KeyValue>
          <KeyValue k="更新时间">{dateLabel(row.updated_at)}</KeyValue>
        </div>
      )}
      {row.description_es ? <div className="notice info">西语描述：{row.description_es}</div> : null}
      {!editing ? <RelatedBoms code={row.code} /> : null}
    </div>
  );
}

// —— 新建物料表单（当页内联，非抽屉） ——
function NewItemForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const { user } = useAuth();
  const seeCost = canManagePurchaseCost(user);
  const [f, setF] = useState<Record<string, string>>({
    item_type: 'product', stock_uom: 'Unidad', currency: 'USD',
    inventory_policy: 'normal', minimum_qty: '0', reorder_qty: '0', warning_enabled: '0',
  });
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { groups: itemGroups, error: groupError } = useItemGroupOptions();
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const genCode = async () => {
    setErr(null);
    if (!f.item_group?.trim()) { setErr('请先选择物料组，编码按物料组匹配规则生成'); return; }
    setGenBusy(true);
    try {
      const res = await api.post<{ data: { code: string } }>('/api/items/next-code', { item_group: f.item_group, item_name: f.name_zh || f.item_group });
      set('code', res.data.code);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '生成编码失败');
    } finally { setGenBusy(false); }
  };

  const submit = async () => {
    setErr(null);
    if (!f.name_zh?.trim()) { setErr('中文名称为必填'); return; }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        name_zh: f.name_zh.trim(),
        name_es: f.name_es || null,
        description_es: f.description_es || null,
        item_group: f.item_group || null,
        item_type: f.item_type,
        stock_uom: f.stock_uom || 'Unidad',
        currency: f.currency || 'USD',
        sale_price: Number(f.sale_price || 0),
        // 采购成本仅财务/管理员可写；其余角色不下发该字段，避免后端 403
        ...(seeCost ? { purchase_cost: Number(f.purchase_cost || 0) } : {}),
        serial_tracked: f.serial_tracked === '1',
        batch_tracked: f.batch_tracked === '1',
        inventory_policy: f.inventory_policy || 'normal',
        minimum_qty: Number(f.minimum_qty || 0),
        reorder_qty: Number(f.reorder_qty || 0),
        warning_enabled: f.warning_enabled === '1',
      };
      if (f.code?.trim()) body.code = f.code.trim();
      await api.post('/api/items', body);
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  const fld = (k: string, label: string, type = 'text', required = false) => (
    <label className="field"><label>{label}{required ? ' *' : ''}</label>
      <input className="txt" type={type} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建物料</h2><div className="desc">写入核心服务 /api/items · 编码留空将按物料组规则自动生成</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
          <label className="field"><label>物料编码（可自动生成）</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="txt" value={f.code ?? ''} onChange={(e) => set('code', e.target.value)} placeholder="留空则生成" style={{ flex: 1 }} />
              <button className="btn" onClick={genCode} disabled={genBusy}>{genBusy ? '生成中…' : '生成'}</button>
            </div>
          </label>
          <label className="field"><label>物料组</label>
            <select className="sel" value={f.item_group ?? ''} onChange={(e) => set('item_group', e.target.value)}>
              <option value="">请选择物料组</option>
              {itemGroups.map((group) => <option key={group} value={group}>{group}</option>)}
            </select>
            {groupError ? <span className="field-hint">{groupError}</span> : null}
          </label>
          {fld('name_zh', '中文名称', 'text', true)}
          {fld('name_es', '西语名称')}
          <label className="field"><label>物料类型</label>
            <select className="sel" value={f.item_type} onChange={(e) => set('item_type', e.target.value)}>
              {Object.entries(TYPE_LABELS).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
            </select>
          </label>
          <label className="field"><label>库存策略</label>
            <select
              className="sel"
              value={f.inventory_policy}
              onChange={(e) => {
                const policy = e.target.value;
                setF((d) => ({
                  ...d,
                  inventory_policy: policy,
                  stock_uom: policy === 'powder' ? 'g' : (d.stock_uom === 'g' ? 'Unidad' : d.stock_uom),
                  warning_enabled: policy === 'powder' || policy === 'consumable' ? '1' : d.warning_enabled,
                }));
              }}
            >
              {Object.entries(POLICY_LABELS).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
            </select>
          </label>
          {fld('stock_uom', '库存单位')}
          {fld('minimum_qty', '最低库存', 'number')}
          {fld('reorder_qty', '补货量', 'number')}
          <label className="field"><label>启用预警</label>
            <select className="sel" value={f.warning_enabled} onChange={(e) => set('warning_enabled', e.target.value)}>
              <option value="0">否</option>
              <option value="1">是</option>
            </select>
          </label>
          {fld('currency', '币种')}
          {fld('sale_price', '销售价格', 'number')}
          {seeCost ? fld('purchase_cost', '采购成本', 'number') : null}
          {fld('description_es', '西语描述')}
          <label className="field"><label>追踪方式</label>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingTop: 6 }}>
              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5 }}>
                <input type="checkbox" checked={f.serial_tracked === '1'} onChange={(e) => set('serial_tracked', e.target.checked ? '1' : '0')} />序列号
              </label>
              <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5 }}>
                <input type="checkbox" checked={f.batch_tracked === '1'} onChange={(e) => set('batch_tracked', e.target.checked ? '1' : '0')} />批次
              </label>
            </div>
          </label>
          <div className="detail-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建物料'}</button>
            <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ItemsPage() {
  const { user, isAdmin } = useAuth();
  const [sp] = useSearchParams();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'items', 'create');
  const seeCost = canManagePurchaseCost(user);
  const itemFields = RECORD_FIELDS['items'].filter((f) => seeCost || f.key !== 'purchase_cost');

  const columns: Column<Item>[] = [
    { header: '物料编码', render: (i) => <span className="num strong">{i.code}</span> },
    // 中/西文名称都必须限宽（2026-10-29 现场：西语名称列过宽，物料组显示不全）。
    // 西语名普遍比中文长一倍（「Rectificado fino Vástago 4 1/16"」），
    // 不限宽时浏览器会把空间全给它，把右侧「物料组」压成省略号。
    // 两者各给一个上限，物料组/类型/单位才有固定空间；完整名称放 title。
    { header: '中文名称', render: (i) => <span className="clip" style={{ maxWidth: '16ch' }} title={i.name_zh || ''}>{i.name_zh || '—'}</span> },
    { header: '西语名称', render: (i) => <span className="muted clip" style={{ maxWidth: '22ch' }} title={i.name_es || ''}>{i.name_es || '—'}</span> },
    { header: '物料组', render: (i) => <span className="clip" style={{ maxWidth: '14ch' }} title={i.item_group || ''}>{i.item_group || '—'}</span> },
    { header: '类型', render: (i) => <span className="muted">{TYPE_LABELS[i.item_type] || i.item_type}</span> },
    { header: '单位', render: (i) => <span className="muted">{i.stock_uom}</span> },
    { header: '销售价格', align: 'right', render: (i) => <span className="num">{money(i.sale_price, i.currency)}</span> },
    { header: '状态', render: (i) => <Pill tone={i.active ? 'ok' : 'neutral'} label={i.active ? '启用' : '停用'} /> },
  ];

  return (
    <>
      {creating ? (
        <NewItemForm
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); setReloadSignal((n) => n + 1); }}
        />
      ) : null}
      <ListPage<Item>
        title="物料"
        desc="物料主数据 · /api/items，支持关键字/物料组搜索、当页展开详情、管理员就地编辑与新建；详情含以其为成品的 BOM 工序"
        endpoint="/api/items"
        columns={columns}
        rowId={(i) => i.id}
        showYear={false}
        searchPlaceholder="搜索编码 / 中文名 / 西语名 / 物料组…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="items" row={row} fields={itemFields} label={String(row.name_zh)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建物料'}</button>
        ) : undefined}
        renderDetail={(row) => <ItemDetail row={row} onSaved={() => setReloadSignal((n) => n + 1)} />}
      />
      {!isAdmin && canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>你可以新建物料；就地编辑既有记录需管理员权限（后端 /api/records 限管理员）。</div>
      ) : null}
    </>
  );
}
